import { DefaultTermSerializer, Specification, Term } from '@/domains/specification/services/impls/specification';
import { injectService } from '@patsnap/tahiti-core';
import { useDisposes } from '@telecom/shared/composables/use-disposes';
import { createSharedComposable } from '@vueuse/core';
import { computed, readonly, ref } from 'vue';
import { SpecificationDataService } from '@/domains/specification/services/specification-data-service';
import { AnnotationManagerBinding } from '@telecom/shared/utils/annotation-tool/interfaces/annotation-manager';
import type { AnnotationManager } from '@telecom/shared/utils/annotation-tool/services/annotation-manager';
import { TermService } from '@/domains/specification/services/term-service';

// eslint-disable-next-line max-lines-per-function
export const useSpecTerms = createSharedComposable(() => {
    const spec = injectService<Specification>(Specification);
    const terms = ref<Term[]>(spec.getAllTerms());

    const { disposes } = useDisposes();

    disposes.push(
        spec.onTermsChange(value => {
            terms.value = value;
        }),
        spec.onTermsChange((terms) => {
            terms.forEach(term => {
                updateAnnotationNumber(term);
            });
        }),
    );

    function addTerm(text: string, number?: string) {
        const termService = injectService<TermService>(TermService);
        const term = new Term({
            id: '',
            text,
        });
        if (number) {
            term.setNumber(number);
        }
        termService.addTerm(term, true);
    }

    function removeTerm(term: Term) {
        spec.removeTerm(term.getId());
    }

    function findTerm(id: string): Term | undefined {
        return spec.findTermById(id);
    }

    const currentTerm = ref<Term | null>(null);

    function setCurrentTerm(term: Term | null) {
        currentAnnotateTerm.value = null;
        currentTerm.value = term;
    }

    function updateAnnotationNumber(term: Term) {
        const annotationManager: AnnotationManager = injectService<AnnotationManager>(AnnotationManagerBinding);
        term.annotations.forEach(annotation => {
            annotationManager?.updateAnnotationStyle(annotation.id, {
                text: term.number,
            });
        });
    }

    const currentAnnotateTerm = ref<Term | null>(null);

    function setCurrentAnnotateTerm(term: Term | null) {
        currentTerm.value = term;
        currentAnnotateTerm.value = term;
    }

    const specDataService = injectService<SpecificationDataService>(SpecificationDataService);
    const termSerializer = new DefaultTermSerializer();

    const isLoadingTerms = ref(false);

    async function loadTerms() {
        isLoadingTerms.value = true;
        const data = await specDataService.fetchTerms().finally(() => {
            isLoadingTerms.value = false;
        });
        const terms = data.data.map((term: any) => termSerializer.deserialize(term));
        terms.forEach(term => {
            spec.addTerm(term);
        });
    }

    const pendingAnnotationTermsCount = computed(() => {
        return terms.value.filter(term => term.number && term.annotations.length === 0).length;
    });

    return {
        terms,
        addTerm,
        removeTerm,
        findTerm,
        setCurrentTerm,
        currentTerm,
        /**
         * 设置当前标注的术语
         * 用于在标注时，设置当前标注的术语
         */
        setCurrentAnnotateTerm,
        currentAnnotateTerm: readonly(currentAnnotateTerm),
        isLoadingTerms,
        loadTerms,
        pendingAnnotationTermsCount,
    };
});
